/**
 * Fall-detection radar catalog (2026-06-11).
 *
 * Added on request from Vetus Schola (ZA distributor) — Hikvision's
 * "Auxiliary Care Radar" line: privacy-preserving mmWave sensors for
 * elderly-care / AAL rooms and bathrooms (no camera, point-cloud only).
 *
 * Category 'fall-detection-radar' rides the PIR placement machinery
 * (CATEGORY_TO_EQUIPMENT_TYPE maps it to 'pir-motion-sensor'), so the
 * wall-mount wedge renders through PIRFOVRenderer with pattern 'cone'.
 *
 * Specs verified against the official Hikvision datasheet
 * (DS-TDSB00-EKT_POE_4M_Datasheet_20230919.pdf) + the live hikvision.com
 * product page on 2026-06-11. NOTE: hikvision.com badges the model
 * "Discontinued" — it remains widely available as channel stock, which is
 * why the distributor still quotes it. Revisit if Hikvision ships a
 * successor.
 */
import type { DetectionSensorCatalogEntry } from './detectionSensorCatalog';

export const FALL_DETECTION_SENSORS: (DetectionSensorCatalogEntry & {
  tagline: string;
  verified: boolean;
})[] = [
  {
    id: 'hikvision-ds-tdsb00-ekt-poe-4m',
    manufacturer: 'Hikvision',
    model: 'DS-TDSB00-EKT/POE/4M',
    category: 'fall-detection-radar',
    // Wall-mount sensor, ±45° horizontal FOV → 90° cone. Datasheet
    // detection area is 5 m (width) × 4 m (depth from wall); rangeM
    // carries the 4 m depth, the 90° wedge approximates the width.
    pattern: 'cone',
    rangeM: 4,
    hfovDeg: 90,
    // Official manual prescribes exactly 2 m wall mounting, horizontally
    // centred on the watched area, radar face tilted 45° down.
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.0,
    mounting: ['wall'],
    detectionMethod: 'radar',
    batteryOperated: false,
    ipRating: 'IP54',
    operatingTempMinC: -10,
    operatingTempMaxC: 50,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000072584/DS-TDSB00-EKT_POE_4M_Datasheet_20230919.pdf',
    tagline:
      '60-63 GHz mmWave fall + presence radar, no camera (privacy-safe), 4 × 5 m zone, wall 2 m, PoE 802.3af / USB-C, Wi-Fi + RJ45, IP54 — care sector; EOL at Hikvision, sold as channel stock',
    verified: true,
  },
  {
    id: 'hikmicro-hm-td1018-1-qr',
    manufacturer: 'HIKMICRO',
    model: 'HM-TD1018-1/QR',
    category: 'fall-detection-radar',
    // Dual-tech care sensor: 160×120 VOx thermal module + 60–64 GHz FMCW
    // radar, no visible-light camera (privacy-safe). Wall/corner mount at
    // 2.7 m; thermal FOV 90°(H) → 90° cone. rangeM carries the 4.5 m
    // room/bed/overstay detection distance (fall-detection reach is 3.5 m).
    pattern: 'cone',
    rangeM: 4.5,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.7,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'radar',
    batteryOperated: false,
    ipRating: 'IP40',
    tagline:
      '160×120 thermal + 60–64 GHz FMCW radar, no visible camera (privacy-safe): fall / out-of-bed / out-of-room / overstay; wall-corner 2.7 m, 4.5 m zone, PoE 802.3af (≤6 W) / DC12V, RS-485, mic+speaker, IP40 — care/AAL sector',
    verified: true,
  },
];
